import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShieldCheck, RefreshCw, AlertTriangle, ScanSearch } from "lucide-react";

const BUCKET_META = {
  verified:  { label: "Stripe-verified", tone: "text-olive-bright",  desc: "Stored Stripe session id" },
  exempt:    { label: "Exempt (staff)",  tone: "text-muted-foreground", desc: "Internal — never says verified" },
  unbacked:  { label: "Unbacked",        tone: "text-[#e07a5f]",     desc: "Flag true · NO Stripe backing" },
  demo_test: { label: "Demo / Test",     tone: "text-amber-400",     desc: "Excluded from the real buckets" },
};

const RESOLVE_TONE = {
  verified: "text-olive-bright", not_verified: "text-amber-400",
  orphaned: "text-[#e07a5f]", unreachable: "text-amber-400", no_session: "text-muted-foreground",
};

function AccountList({ rows, testid }) {
  if (!rows?.length) return <p className="font-mono text-[11px] uppercase text-muted-foreground px-3 py-2">None.</p>;
  return (
    <div className="divide-y divide-sniper/50" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.user_id} className="grid grid-cols-[1.4fr_1fr_auto_auto] items-center gap-2 px-3 py-1.5 font-mono text-[11px]" data-testid={`taxonomy-row-${r.user_id}`}>
          <span className="text-foreground truncate">@{r.username}</span>
          <span className="text-muted-foreground truncate">{r.email}</span>
          <span className={`uppercase text-[9px] px-1.5 py-0.5 border ${r.provenance === "unknown" ? "border-[#e07a5f]/50 text-[#e07a5f]" : "border-sniper text-muted-foreground"}`}>{r.provenance}</span>
          <span className="text-secondary-foreground tabular-nums text-right">{r.active_listing_count} live</span>
        </div>
      ))}
    </div>
  );
}

export default function VerificationTaxonomyAudit() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveData, setResolveData] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/verification/taxonomy");
      setData(res.data);
      setResolveData(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Detector failed.");
    } finally { setBusy(false); }
  };

  const resolve = async () => {
    setResolving(true);
    try {
      const res = await api.post("/admin/verification/taxonomy/resolve", {});
      setResolveData(res.data);
      if (res.data.key_status !== "valid") {
        toast.message(res.data.note || "Live resolve unavailable.");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Live resolve failed.");
    } finally { setResolving(false); }
  };

  const c = data?.counts || {};
  const unbackedUnknown = data?.unbacked_by_provenance?.unknown ?? 0;

  return (
    <div className="mt-4 border border-sniper bg-surface p-5" data-testid="verification-taxonomy-audit">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-olive-bright" /> ID Verification Taxonomy
          </h3>
          <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 max-w-xl">
            Read-only. Sorts every ID-verified account into Stripe-verified / exempt / unbacked / demo-test —
            the buckets always sum to the total flagged. UNBACKED "unknown" accounts carry the flag with no
            Stripe backing and no known origin.
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="taxonomy-run"
          className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-4 py-2 text-sm transition-colors">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scanning…" : (data ? "Re-scan" : "Run detector")}
        </button>
      </div>

      {data && (
        <div className="mt-4 space-y-4" data-testid="taxonomy-results">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-[11px] uppercase">
            <span className="text-muted-foreground">Total flagged: <span className="text-foreground tabular-nums" data-testid="taxonomy-total">{data.total_flagged}</span></span>
            <span className={data.sum_check ? "text-olive-bright" : "text-[#e07a5f]"} data-testid="taxonomy-sumcheck">
              {data.sum_check ? "✓ buckets sum to total" : "✗ SUM MISMATCH"}
            </span>
          </div>

          {/* Bucket counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="taxonomy-counts">
            {["verified", "exempt", "unbacked", "demo_test"].map((k) => (
              <div key={k} className="border border-sniper px-3 py-2" data-testid={`taxonomy-count-${k}`}>
                <div className={`font-heading text-2xl tabular-nums ${BUCKET_META[k].tone}`}>{c[k] ?? 0}</div>
                <div className="font-mono text-[10px] uppercase text-secondary-foreground">{BUCKET_META[k].label}</div>
                <div className="font-mono text-[9px] uppercase text-muted-foreground/70 leading-tight mt-0.5">{BUCKET_META[k].desc}</div>
              </div>
            ))}
          </div>

          {/* UNBACKED — the actionable bucket, split by provenance */}
          {c.unbacked > 0 && (
            <div className="border border-[#e07a5f]/50" data-testid="taxonomy-unbacked-block">
              <div className="px-3 py-2 border-b border-[#e07a5f]/40 flex items-center gap-2 bg-[#e07a5f]/10">
                <AlertTriangle className="w-4 h-4 text-[#e07a5f]" />
                <span className="font-mono text-[11px] uppercase text-[#e0a090]">
                  {c.unbacked} unbacked · <span className="text-[#e07a5f] font-bold" data-testid="taxonomy-unknown-count">{unbackedUnknown} unknown origin</span> ({data.unbacked_by_provenance?.seed ?? 0} seed)
                </span>
              </div>
              <AccountList rows={data.buckets?.unbacked} testid="taxonomy-unbacked-list" />
            </div>
          )}

          {/* Exempt (staff) */}
          {c.exempt > 0 && (
            <div className="border border-sniper" data-testid="taxonomy-exempt-block">
              <div className="px-3 py-2 border-b border-sniper font-mono text-[11px] uppercase text-muted-foreground">Exempt (internal / staff) — never shown as verified</div>
              <AccountList rows={data.buckets?.exempt} testid="taxonomy-exempt-list" />
            </div>
          )}

          {/* Live Stripe resolve — required before acting on an account */}
          <div className="border border-sniper" data-testid="taxonomy-resolve-block">
            <div className="px-3 py-2 border-b border-sniper flex items-center justify-between gap-2 flex-wrap">
              <span className="font-mono text-[11px] uppercase text-secondary-foreground flex items-center gap-2">
                <ScanSearch className="w-4 h-4 text-olive-bright" /> Live Stripe resolve
              </span>
              <button onClick={resolve} disabled={resolving} data-testid="taxonomy-resolve-run"
                className="inline-flex items-center gap-2 border border-olive-bright/50 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-mono text-[10px] uppercase px-3 py-1.5 transition-colors">
                <RefreshCw className={`w-3 h-3 ${resolving ? "animate-spin" : ""}`} /> {resolving ? "Resolving…" : "Resolve stored sessions"}
              </button>
            </div>
            <p className="px-3 py-2 font-mono text-[10px] uppercase text-muted-foreground/80 leading-relaxed">
              Verifies each stored Stripe session id against the live account — a stored id proves someone wrote an id,
              not that Stripe verified a human. Required before any destructive action.
            </p>
            {resolveData && (
              <div className="px-3 pb-3 space-y-2" data-testid="taxonomy-resolve-results">
                <div className="font-mono text-[11px] uppercase">
                  Key: <span className={resolveData.key_status === "valid" ? "text-olive-bright" : "text-amber-400"} data-testid="taxonomy-resolve-key">{resolveData.key_status}</span>
                  {" · "}checked <span className="text-foreground">{resolveData.checked}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px]">
                  {Object.entries(resolveData.tally || {}).map(([k, v]) => (
                    <span key={k} className={RESOLVE_TONE[k] || "text-muted-foreground"}>{k.replace("_", " ")}: {v}</span>
                  ))}
                </div>
                {resolveData.note && <p className="font-mono text-[10px] uppercase text-muted-foreground">{resolveData.note}</p>}
              </div>
            )}
          </div>

          <p className="font-mono text-[10px] uppercase text-muted-foreground/70 leading-relaxed" data-testid="taxonomy-note">{data.note}</p>
        </div>
      )}
    </div>
  );
}
